/**
 * ConsumptionInformationPanel — native rewrite of the legacy ConsumptionInformation
 * sub-tab (vacancy, renewable energies, energetic state).
 *
 * Vacancy start/end (moment) keep leaf-local `useState` and the area is a `NumericFloatField`
 * (legacy held them in constate); every edit re-derives the wire `vacancy` object via
 * the codec's `encodeVacancy` (single period only when vacant AND area entered — legacy
 * parity) and commits it. The energetic-condition Set toggle goes through `applyDerived`
 * → `patchEnergeticConditions`, which re-derives `energeticRenovations` from the LIVE DTO.
 */

import React, { Fragment, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import moment, { type Moment } from 'moment-timezone'

import { GRAY_900 } from 'constants/styling/theme'
import {
  EnergeticMeasure,
  RenewableEnergySource,
  RenewableEnergySourceUsage
} from 'constants/purchaseFlow/consumptionCertificateTypes'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUIDatePicker } from 'components/common/MUIDatePicker'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { SelectableButtonsGrid } from 'components/common/SelectableButtonsGrid'
import Stack from '@mui/material/Stack'
import { ToggleButton } from 'components/common/ToggleButton'
import type { ToggleFilterValue } from 'components/common/ToggleFilter'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useConsumptionCertificateConfig } from '../useConsumptionCertificateConfig'
import { encodeVacancy, patchEnergeticConditions } from '../consumptionCertificate.codec'
import { usePeriodPickerBounds } from './usePeriodPickerBounds'

const DATE_FORMAT = 'DD.MM.YYYY'
const momentOrNull = (v: string | undefined, format: string): Moment | null => {
  if (!v) return null
  const m = moment(v, format)
  return m.isValid() ? m : null
}

export interface ConsumptionInformationPanelProps {
  cartItemId: CartItemId
}

export const ConsumptionInformationPanel: React.FC<ConsumptionInformationPanelProps> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow', 'common'], {
    keyPrefix: 'consumption_certificate_step.consumption_information_page',
  })
  const { value, patch, applyDerived } = useConsumptionCertificateConfig(cartItemId)

  const [showError, setShowError] = useState(false)
  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)
  }

  // Leaf-local vacancy working fields (seeded once from the persisted period[0]).
  const period0 = value.vacancy?.vacancyPeriods?.[0]
  const [startDate, setStartDate] = useState<Moment | null>(() => momentOrNull(period0?.startVacancy, DATE_FORMAT))
  const [endDate, setEndDate] = useState<Moment | null>(() => momentOrNull(period0?.endVacancy, DATE_FORMAT))
  const area = period0?.areaVacancyInSquareMeters ? String(period0.areaVacancyInSquareMeters) : ''

  const hasPropertyVacant = value.vacancy?.vacancyPresent ?? true
  const vacancyBounds = usePeriodPickerBounds(startDate, endDate)
  const systemType = value.renewableEnergies ?? RenewableEnergySource.NONE
  const useType = value.renewableEnergiesUsage ?? RenewableEnergySourceUsage.NONE
  const energeticConditions = value.currentEnergeticConditions ?? []

  const commitVacancy = (next: { present?: boolean; start?: Moment | null; end?: Moment | null; areaStr?: string }) => {
    const present = next.present ?? hasPropertyVacant
    const start = next.start !== undefined ? next.start : startDate
    const end = next.end !== undefined ? next.end : endDate
    const areaStr = next.areaStr !== undefined ? next.areaStr : area
    patch({ vacancy: encodeVacancy({ present, area: areaStr, start, end }) })
  }

  const handleSetVacant = (present: boolean) => commitVacancy({ present })
  const handleSetStart = (v: Moment | null) => {
    setStartDate(v)
    commitVacancy({ start: v })
  }
  const handleSetEnd = (v: Moment | null) => {
    setEndDate(v)
    commitVacancy({ end: v })
  }
  const handleSetArea = (raw: string) => commitVacancy({ areaStr: raw })

  const handleSetSystemType = (type: RenewableEnergySource) => {
    // Legacy reset: when the system is NONE, force usage to NONE too.
    if (type === RenewableEnergySource.NONE) {
      patch({ renewableEnergies: type, renewableEnergiesUsage: RenewableEnergySourceUsage.NONE })
    } else {
      patch({ renewableEnergies: type })
    }
  }

  const useTypeToggleValues: ToggleFilterValue<RenewableEnergySourceUsage>[] = Object.values(RenewableEnergySourceUsage)
    .filter((type) =>
      systemType !== RenewableEnergySource.NONE ? type !== RenewableEnergySourceUsage.NONE : type === RenewableEnergySourceUsage.NONE
    )
    .map((type) => ({ value: type, displayName: t(`use_type.${type}`) }))

  const energeticStateToggleValues: ToggleFilterValue<EnergeticMeasure>[] = Object.values(EnergeticMeasure).map((state) => ({
    value: state,
    displayName: t(`energetic_condition_type.${state}`),
  }))

  return (
    <Fragment>

      {/* Vacancy */}
      <SectionedBorderBox title={t('vacancy')}>
        <Stack gap="1.8rem" width={{ xs: '100%', sm: '40rem', lg: '50rem' }} position="relative">

          <Stack gap=".6rem">

            <Label text={t('has_property_been_vacant')} />

            <Stack gap=".8rem" flexDirection="row">

              <ToggleButton
                sx={{ width: '6.4rem', justifyContent: 'center' }}
                isSelected={hasPropertyVacant === true}
                label={t('yes')}
                onClick={() => handleSetVacant(true)}
              />

              <ToggleButton
                sx={{ width: '6.4rem', justifyContent: 'center' }}
                isSelected={hasPropertyVacant === false}
                label={t('no')}
                onClick={() => handleSetVacant(false)}
              />

            </Stack>

          </Stack>

          {hasPropertyVacant && <>

            <Stack gap=".6rem">

              <Label text={t('period_of_vacancy')} />

              <Stack flexDirection="row" gap={1} alignItems="center" flexWrap="wrap">

                <MUIDatePicker
                  required
                  value={startDate}
                  views={['year', 'month', 'day']}
                  format="DD/MM/YYYY"
                  minDate={vacancyBounds.startMinDate}
                  maxDate={vacancyBounds.startMaxDate}
                  isError={(showError && !startDate) || vacancyBounds.isRangeInvalid}
                  onInputBlur={handleOnInputBlur}
                  onChange={(newValue) => handleSetStart(newValue)}
                />

                <Typography variant="text-md" color={GRAY_900}>
                  {t('common:to')}
                </Typography>

                <MUIDatePicker
                  required
                  value={endDate}
                  views={['year', 'month', 'day']}
                  format="DD/MM/YYYY"
                  minDate={vacancyBounds.endMinDate}
                  maxDate={vacancyBounds.endMaxDate}
                  isError={(showError && !endDate) || vacancyBounds.isRangeInvalid}
                  onInputBlur={handleOnInputBlur}
                  onChange={(newValue) => handleSetEnd(newValue)}
                />

              </Stack>

            </Stack>

            <Box width={{ xs: '80%', lg: '45%' }}>
              <NumericFloatField
                required
                label={t('area')}
                suffix="m2"
                inputBoxWidth="100%"
                value={area}
                isError={showError && !area}
                onBlur={handleOnInputBlur}
                onCommit={handleSetArea}
              />
            </Box>

          </>}

        </Stack>
      </SectionedBorderBox>

      {/* Renewable energies */}
      <SectionedBorderBox title={t('renewable_energies')}>
        <Stack gap="1.8rem" width={{ xs: '100%', sm: '40rem', lg: '50rem' }} position="relative">

          <MUIDropdown
            sx={{ width: '100%' }}
            button={(_, action) => (
              <MUIDropdownInput
                readOnly
                label={t('system')}
                type={DropdownInputTypes.SELECTOR}
                value={t(systemType ? `system_type.${systemType}` : 'select_system_type_option')}
                onClick={action}
              />
            )}
          >
            {Object.values(RenewableEnergySource).map((type) => (
              <MUIDropdownItem key={type} onClick={() => handleSetSystemType(type)}>
                <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                  {t(`system_type.${type}`)}
                </Typography>
              </MUIDropdownItem>
            ))}
          </MUIDropdown>

          {systemType !== RenewableEnergySource.NONE &&
            <Stack gap=".6rem">

              <Label text={t('use')} />

              <SelectableButtonsGrid
                values={useTypeToggleValues}
                selectedValue={useType}
                onSelect={(type) => patch({ renewableEnergiesUsage: type })}
                onUnselect={() => patch({ renewableEnergiesUsage: RenewableEnergySourceUsage.NONE })}
                showRadio
                activeButtonClickable
              />

            </Stack>
          }

        </Stack>
      </SectionedBorderBox>

      {/* Energetic current state */}
      <SectionedBorderBox title={t('energetic_state')}>
        <Stack gap="1.8rem" position="relative">
          <Stack gap=".6rem">

            <Label text={t('applicable_points')} />

            <SelectableButtonsGrid
              values={energeticStateToggleValues}
              selectedValue={energeticConditions}
              onSelect={(type) => applyDerived((c) => patchEnergeticConditions(c, [...(c.currentEnergeticConditions ?? []), type]))}
              onUnselect={(type) =>
                applyDerived((c) =>
                  patchEnergeticConditions(c, (c.currentEnergeticConditions ?? []).filter((item) => item !== type))
                )
              }
              showCheckbox
              activeButtonClickable
            />

          </Stack>
        </Stack>
      </SectionedBorderBox>

    </Fragment>
  )
}
